
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import GuestManagement from "../../../GuestManagement";
import { cn } from "@/lib/utils";

interface Guest {
  name: string;
  email: string;
  phone: string;
}

interface ContactFormStepProps {
  formData: {
    name: string;
    email: string;
    company: string;
    message: string;
  };
  customAnswers: Record<string, string>;
  guests: Guest[];
  onInputChange: (field: string, value: string) => void;
  onCustomAnswerChange: (questionId: string, value: string) => void;
  onGuestsChange: (guests: Guest[]) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack?: () => void;
  customQuestions?: any[];
  loading?: boolean;
  error?: string | null;
  className?: string;
  isRescheduleMode?: boolean;
  isCancelMode?: boolean;
}

export const ContactFormStep: React.FC<ContactFormStepProps> = React.memo(({
  formData,
  customAnswers,
  guests,
  onInputChange,
  onCustomAnswerChange,
  onGuestsChange,
  onSubmit,
  onBack,
  customQuestions = [],
  loading = false,
  error = null,
  className,
  isRescheduleMode = false,
  isCancelMode = false
}) => {
  // Memoize whether we have valid custom questions to prevent re-renders
  const hasValidCustomQuestions = React.useMemo(() => {
    return Array.isArray(customQuestions) && customQuestions.length > 0;
  }, [customQuestions]);

  // Memoize the custom questions rendering to prevent re-renders
  const customQuestionsSection = React.useMemo(() => {
    if (!hasValidCustomQuestions) {
      return null;
    }
    
    return (
      <div className="border-t pt-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
        <div className="space-y-4">
          {customQuestions.map((question, index) => {
            if (!question) {
              return null;
            }
            
            // Support both id and label as the unique identifier
            const questionId = question.id || question.label || `question_${index}`;
            const questionText = question.question || question.label;
            
            if (!questionText) {
              return null;
            }

            const questionType = (question.type || question.field_type || 'text').toLowerCase();
            const isRequired = Boolean(question.required);
            const placeholder = question.placeholder || '';
            const currentValue = customAnswers[questionId] || '';

            return (
              <div key={questionId}>
                <Label htmlFor={`custom-${questionId}`}>
                  {questionText} {isRequired ? "*" : ""}
                </Label>
                
                {questionType === "textarea" ? (
                  <Textarea
                    id={`custom-${questionId}`}
                    value={currentValue}
                    onChange={(e) => onCustomAnswerChange(questionId, e.target.value)}
                    placeholder={placeholder}
                    required={isRequired}
                    className="min-h-[80px]"
                  />
                ) : questionType === "select" || questionType === "dropdown" ? (
                  <Select
                    value={currentValue}
                    onValueChange={(value) => onCustomAnswerChange(questionId, value)}
                    required={isRequired}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={placeholder || "Select an option"} />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(question.options) && question.options.length > 0 ? (
                        question.options.map((option, optionIndex) => {
                          const optionValue = typeof option === 'string' ? option : option.label || option.value;
                          const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                          return (
                            <SelectItem key={optionIndex} value={optionValue}>
                              {optionLabel}
                            </SelectItem>
                          );
                        })
                      ) : (
                        <SelectItem value="no-options" disabled>
                          No options available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                ) : questionType === "radio" ? (
                  <RadioGroup
                    value={currentValue}
                    onValueChange={(value) => onCustomAnswerChange(questionId, value)}
                    required={isRequired}
                    className="space-y-2"
                  >
                    {Array.isArray(question.options) && question.options.length > 0 ? (
                      question.options.map((option, optionIndex) => {
                        const optionValue = typeof option === 'string' ? option : option.label || option.value;
                        const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                        return (
                          <div key={optionIndex} className="flex items-center space-x-2">
                            <RadioGroupItem value={optionValue} id={`${questionId}-${optionIndex}`} />
                            <Label htmlFor={`${questionId}-${optionIndex}`} className="text-sm font-normal">
                              {optionLabel}
                            </Label>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-sm text-gray-500">No options available</div>
                    )}
                  </RadioGroup>
                ) : questionType === "checkbox" ? (
                  <div className="space-y-2">
                    {Array.isArray(question.options) && question.options.length > 0 ? (
                      question.options.map((option, optionIndex) => {
                        const optionValue = typeof option === 'string' ? option : option.label || option.value;
                        const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                        const isChecked = currentValue.includes(optionValue);
                        
                        return (
                          <div key={optionIndex} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${questionId}-${optionIndex}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                const currentValues = currentValue ? currentValue.split(',') : [];
                                if (checked) {
                                  if (!currentValues.includes(optionValue)) {
                                    currentValues.push(optionValue);
                                  }
                                } else {
                                  const index = currentValues.indexOf(optionValue);
                                  if (index > -1) {
                                    currentValues.splice(index, 1);
                                  }
                                }
                                onCustomAnswerChange(questionId, currentValues.join(','));
                              }}
                            />
                            <Label htmlFor={`${questionId}-${optionIndex}`} className="text-sm font-normal">
                              {optionLabel}
                            </Label>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-sm text-gray-500">No options available</div>
                    )}
                  </div>
                ) : (
                  <Input
                    id={`custom-${questionId}`}
                    type={
                      questionType === "number" ? "number" :
                      questionType === "email" ? "email" :
                      questionType === "tel" ? "tel" :
                      questionType === "url" ? "url" :
                      "text"
                    }
                    value={currentValue}
                    onChange={(e) => onCustomAnswerChange(questionId, e.target.value)}
                    placeholder={placeholder}
                    required={isRequired}
                    className="h-11"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [hasValidCustomQuestions, customQuestions, customAnswers, onCustomAnswerChange]);

  return (
    <div className={cn("flex flex-col h-full", className)} data-testid="contact-form">
      {/* Fixed Header */}
      <div className="flex-shrink-0 mb-6 px-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {isRescheduleMode ? 'Reschedule your meeting' : isCancelMode ? 'Cancel your meeting' : 'Enter contact info'}
        </h2>
        <p className="text-gray-600">
          Please provide your information to confirm the booking.
        </p>
      </div>

      {/* Scrollable Form Content */}
      <form id="contact-form" onSubmit={onSubmit} className="flex-1 overflow-y-auto min-h-0 space-y-6 px-4">
        <div className="space-y-3 p-1">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => onInputChange('name', e.target.value)}
              placeholder="Enter your full name"
              required
              data-testid="name-input"
            />
          </div>

          <div>
            <Label htmlFor="company">Company (Optional)</Label>
            <Input
              id="company"
              type="text"
              value={formData.company}
              onChange={(e) => onInputChange('company', e.target.value)}
              placeholder="Enter your company name"
              data-testid="company-input"
            />
          </div>

          <div>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => onInputChange('email', e.target.value)}
              placeholder="Enter your email address"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <Label htmlFor="guests">Additional Guests (Optional)</Label>
            <GuestManagement guests={guests} setGuests={onGuestsChange} />
          </div>

          <div>
            <Label htmlFor="message">Message (Optional)</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => onInputChange('message', e.target.value)}
              placeholder="Add any additional details or questions..."
              className="min-h-[100px]"
              data-testid="message-input"
            />
          </div>

          {/* Custom Questions Section */}
          {customQuestionsSection}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </form>

      {/* Bottom Buttons - Sticky on mobile with gradient, fixed on desktop */}
      <div className="sticky bottom-0 bg-gradient-to-b from-white/0 to-white border-gray-200 p-2 mx-0 z-10 md:sticky md:bottom-auto md:bg-transparent md:border-t-0 md:p-0 md:pt-4 md:mx-0 md:z-auto">
        <div className="grid grid-cols-2 gap-3 md:flex md:justify-between">
          {/* Back Button - Left column on mobile, left aligned on desktop */}
          {onBack && (
            <Button 
              type="button"
              variant="outline"
              onClick={onBack}
              className="h-12 text-sm font-medium px-3"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          )}
          
          {/* Submit Button - Right column on mobile, right aligned on desktop */}
          <Button 
            type="submit" 
            form="contact-form"
            className="h-12 text-sm font-medium px-3 md:ml-auto"
            disabled={loading || !formData.name || !formData.email}
            data-testid="submit-booking"
            onClick={onSubmit}
          >
            {loading ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                <span className="hidden sm:inline">Confirming Booking...</span>
                <span className="sm:hidden">Confirming...</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Confirm Booking</span>
                <span className="sm:hidden">Confirm</span>
              </>
            )}
          </Button>

        </div>
      </div>
    </div>
  );
});

ContactFormStep.displayName = 'ContactFormStep';
